// Per-room Scenes manager — shared by the standalone Scenes screen (variant
// "page") and the inline strip embedded in the room's device grid (variant
// "inline"). Up to 8 programmable scenes per room; a scene is a snapshot of every
// device's target state, fired as a batch of MQTT commands on tap.
//
// Inline variant (what shows on the home/room page):
//   - a compact horizontal strip of the room's scenes,
//   - LEFT-click a scene  → run it,
//   - RIGHT-click a scene → Run / Edit / Delete,
//   - RIGHT-click the strip background → Add Scene,
//   - the add/edit editor opens in a dialog (stays on the page).
//
// All data + MQTT mapping live in scenesApi; the editor is SceneEditor.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Grid, Card, CardActionArea,
  CircularProgress, Snackbar, Menu, MenuItem, ListItemIcon, ListItemText,
  Dialog, DialogContent, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useApp } from '../../state/AppContext';
import { getDevicesByRoom, getOutputs, getDeviceStatus } from '../../api/api';
import { parseStatus } from '../../mqtt/mqttClient';
import {
  fetchRoomScenes, saveScene, deleteScene, runScene,
  deviceControls, needsOutputs, statusSelections, MAX_SCENES_PER_ROOM,
} from './scenesApi';
import SceneEditor from './SceneEditor';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

export default function RoomScenesManager({
  variant = 'page', roomId, roomName = 'Room', initialDevices = null, onBack,
}) {
  const inline = variant === 'inline';
  const { userId } = useApp();

  const [controls, setControls] = useState([]);        // flat, sceneable-only
  const [sceneable, setSceneable] = useState([]);      // sceneable device objects
  const [outputsByUno, setOutputsByUno] = useState({});
  const [statusByUno, setStatusByUno] = useState({});
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);   // reading live status for a new scene
  const [runningId, setRunningId] = useState(null);
  const [editing, setEditing] = useState(null);        // null = list; else scene draft
  const [menu, setMenu] = useState(null);              // { mouseX, mouseY, scene|null }
  const [toast, setToast] = useState('');

  // Read each device's last-known status → parsed status string.
  const readStatuses = async (devs) => {
    const statuses = {};
    await Promise.all(devs.map(async (d) => {
      const res = await getDeviceStatus(d.UniqueNumber).catch(() => null);
      const row = res && Array.isArray(res.data) ? res.data[0] : null;
      statuses[d.UniqueNumber] = row && row.Status ? parseStatus(row.Status) : '';
    }));
    return statuses;
  };

  // Load the room's devices (from props or fetch), their outputs, and the saved
  // scenes. Live status is read lazily on "Add" (startAdd), not upfront.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let devs = initialDevices;
      if (!devs) {
        devs = asArray(await getDevicesByRoom(userId, roomId).catch(() => null));
      }

      const outByUno = {};
      await Promise.all(devs.filter(needsOutputs).map(async (d) => {
        const res = await getOutputs(d.UniqueNumber).catch(() => null);
        outByUno[d.UniqueNumber] = asArray(res);
      }));
      setOutputsByUno(outByUno);

      const sceneableDevs = devs.filter((d) => deviceControls(d, outByUno[d.UniqueNumber] || []).length > 0);
      setSceneable(sceneableDevs);
      setControls(sceneableDevs.flatMap((d) => deviceControls(d, outByUno[d.UniqueNumber] || [])));

      setScenes(await fetchRoomScenes(userId, roomId).catch(() => []));
    } finally {
      setLoading(false);
    }
  // Reload whenever the room changes (inline strip switches rooms in place).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, roomId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [roomId]);

  const runOne = async (scene) => {
    if (runningId) return;
    setRunningId(scene.id);
    setToast(`Running "${scene.name}"…`);
    try {
      await runScene(userId, scene);
      setToast(`Ran "${scene.name}"`);
    } finally {
      setRunningId(null);
    }
  };

  // Every binary (switch/generic) output is always explicit: default unset to OFF
  // so a scene stores the COMPLETE on/off state of all switches.
  const withBinaryDefaults = (sel) => {
    const out = { ...sel };
    controls.forEach((c) => {
      if (c.binary && (!out[c.id] || out[c.id] === 'ignore')) out[c.id] = 'off';
    });
    return out;
  };

  const selectionsFor = (scene) => {
    const sel = {};
    for (const c of controls) {
      if (c.kind === 'light') {
        // Light: the step's raw comma values ARE the selection (drives sliders).
        const step = (scene?.steps || []).find((s) => s.uniqueNumber === c.uniqueNumber);
        if (step) sel[c.id] = step.message;
        continue;
      }
      const step = (scene?.steps || []).find((s) => s.uniqueNumber === c.uniqueNumber
        && c.options.some((o) => o.message === s.message));
      if (step) {
        const opt = c.options.find((o) => o.message === step.message);
        if (opt) sel[c.id] = opt.key;
      }
    }
    return sel;
  };

  // New scene = a full snapshot of the room's current output states (re-read live
  // on click so it's current, not page-load stale).
  const startAdd = async () => {
    setPreparing(true);
    setToast('Reading device status…');
    const statuses = await readStatuses(sceneable).catch(() => statusByUno);
    setStatusByUno(statuses);
    const selections = {};
    sceneable.forEach((d) => Object.assign(
      selections,
      statusSelections(d, statuses[d.UniqueNumber] || '', outputsByUno[d.UniqueNumber] || []),
    ));
    setPreparing(false);
    setToast('');
    setEditing({ id: null, name: '', color: '#37474F', selections: withBinaryDefaults(selections) });
  };

  const startEdit = (scene) =>
    setEditing({ id: scene.id, name: scene.name, color: scene.color, sortOrder: scene.sortOrder, selections: withBinaryDefaults(selectionsFor(scene)) });

  const onDelete = async (scene) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete scene "${scene.name}"?`)) return;
    setBusy(true);
    await deleteScene(scene.id).catch(() => {});
    await load();
    setBusy(false);
  };

  const saveDraft = async () => {
    const name = (editing.name || '').trim();
    if (!name) { setToast('Give the scene a name'); return; }
    const steps = controls
      .map((c) => {
        const k = editing.selections[c.id];
        if (c.kind === 'light') {
          // Light: selection is the comma-joined channel values (or 'ignore'/unset
          // to leave it out of the scene).
          return k && k !== 'ignore' ? { uniqueNumber: c.uniqueNumber, message: k } : null;
        }
        if (k && k !== 'ignore') {
          const opt = c.options.find((o) => o.key === k);
          return opt ? { uniqueNumber: c.uniqueNumber, message: opt.message } : null;
        }
        if (c.binary) {
          const off = c.options.find((o) => o.key === 'off');
          return off ? { uniqueNumber: c.uniqueNumber, message: off.message } : null;
        }
        return null;
      })
      .filter(Boolean);
    if (steps.length === 0) { setToast('Set at least one device'); return; }
    setBusy(true);
    await saveScene(userId, roomId, {
      id: editing.id, name, color: editing.color,
      sortOrder: editing.sortOrder || scenes.length,
      steps,
    }).catch(() => {});
    setEditing(null);
    await load();
    setBusy(false);
  };

  const setSelection = (controlId, key) => {
    const ctrl = controls.find((c) => c.id === controlId);
    setEditing((e) => {
      const cur = e.selections[controlId];
      const next = ctrl && ctrl.binary ? key : (cur === key ? 'ignore' : key);
      return { ...e, selections: { ...e.selections, [controlId]: next } };
    });
  };

  // Set a raw selection value (used by light sliders: the comma-joined channel
  // values, or 'ignore' to exclude the light from the scene).
  const setValue = (controlId, value) =>
    setEditing((e) => ({ ...e, selections: { ...e.selections, [controlId]: value } }));

  const atMax = scenes.length >= MAX_SCENES_PER_ROOM;
  const noControls = controls.length === 0;

  // ---- context menus (inline) ----
  const openBgMenu = (e) => {
    if (!inline) return;
    e.preventDefault();
    setMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 4, scene: null });
  };
  const openSceneMenu = (e, scene) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 4, scene });
  };
  const closeMenu = () => setMenu(null);

  const editorNode = editing && (
    <SceneEditor
      draft={editing}
      controls={controls}
      busy={busy}
      onName={(name) => setEditing((e) => ({ ...e, name }))}
      onColor={(color) => setEditing((e) => ({ ...e, color }))}
      onToggle={setSelection}
      onSetValue={setValue}
      onSave={saveDraft}
      onCancel={() => setEditing(null)}
    />
  );

  const toastNode = (
    <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
  );

  const contextMenuNode = inline && (
    <Menu
      open={!!menu}
      onClose={closeMenu}
      anchorReference="anchorPosition"
      anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
    >
      {menu && menu.scene
        ? [
          <MenuItem key="run" onClick={() => { runOne(menu.scene); closeMenu(); }}>
            <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Run</ListItemText>
          </MenuItem>,
          <MenuItem key="edit" onClick={() => { startEdit(menu.scene); closeMenu(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>,
          <MenuItem key="del" onClick={() => { onDelete(menu.scene); closeMenu(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>,
        ]
        : (
          <MenuItem
            disabled={atMax || noControls || preparing}
            onClick={() => { startAdd(); closeMenu(); }}
          >
            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>
              {noControls ? 'No scene-able devices'
                : atMax ? `Max ${MAX_SCENES_PER_ROOM} scenes`
                  : 'Add Scene'}
            </ListItemText>
          </MenuItem>
        )}
    </Menu>
  );

  // ---------------------------------------------------------------- INLINE
  if (inline) {
    return (
      <>
        <Box
          onContextMenu={openBgMenu}
          sx={{
            border: '1px dashed', borderColor: 'divider', borderRadius: 2,
            px: 1.25, py: 1, mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: scenes.length ? 0.75 : 0 }}>
            <AutoAwesomeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="overline" color="text.secondary">Scenes</Typography>
            {loading && <CircularProgress size={14} />}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.disabled">
              right-click to {scenes.length ? 'add / edit' : 'add'}
            </Typography>
          </Box>

          {!loading && scenes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              {noControls
                ? 'No scene-programmable devices in this room.'
                : 'No scenes yet — right-click here to add one (max 8).'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {scenes.map((s) => (
                <Tooltip key={s.id} title="Click to run · right-click to edit / delete">
                  <Box
                    onClick={() => runOne(s)}
                    onContextMenu={(e) => openSceneMenu(e, s)}
                    sx={{
                      bgcolor: s.color, color: s.textColor, borderRadius: 2,
                      px: 1.5, py: 0.75, minWidth: 92, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
                      opacity: runningId && runningId !== s.id ? 0.5 : 1,
                      userSelect: 'none',
                    }}
                  >
                    {runningId === s.id
                      ? <CircularProgress size={18} sx={{ color: s.textColor }} />
                      : <AutoAwesomeIcon fontSize="small" />}
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
                      {s.name}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        {/* Add/Edit editor — dialog keeps everything on the room page. */}
        <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
          <DialogContent dividers>{editorNode}</DialogContent>
        </Dialog>

        {contextMenuNode}
        {toastNode}
      </>
    );
  }

  // ------------------------------------------------------------------ PAGE
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => (editing ? setEditing(null) : onBack && onBack())}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            {editing ? (editing.id ? 'Edit Scene' : 'New Scene') : `${roomName} · Scenes`}
          </Typography>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : editing ? (
        editorNode
      ) : (
        <Box sx={{ p: 2 }}>
          {noControls && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This room has no scene-programmable devices (switches, fans, curtains or lights).
            </Typography>
          )}
          <Grid container spacing={2}>
            {scenes.map((s) => (
              <Grid item xs={6} sm={4} md={3} key={s.id}>
                <Card sx={{ bgcolor: s.color, borderRadius: 3, position: 'relative', opacity: runningId && runningId !== s.id ? 0.5 : 1 }}>
                  <CardActionArea onClick={() => runOne(s)} disabled={!!runningId} sx={{ p: 2, minHeight: 96 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: s.textColor }}>
                      {runningId === s.id ? <CircularProgress size={22} sx={{ color: s.textColor }} /> : <AutoAwesomeIcon />}
                      <Typography fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>{s.name}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        {runningId === s.id ? 'Running…' : `${s.steps.length} step${s.steps.length === 1 ? '' : 's'}`}
                      </Typography>
                    </Box>
                  </CardActionArea>
                  <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex' }}>
                    <IconButton size="small" sx={{ color: s.textColor }} onClick={() => startEdit(s)}><EditIcon fontSize="inherit" /></IconButton>
                    <IconButton size="small" sx={{ color: s.textColor }} onClick={() => onDelete(s)}><DeleteIcon fontSize="inherit" /></IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
            {!noControls && (
              <Grid item xs={6} sm={4} md={3}>
                <Card sx={{ borderRadius: 3, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
                  <CardActionArea onClick={startAdd} disabled={atMax || preparing} sx={{ p: 2, minHeight: 96 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: atMax ? 'text.disabled' : 'text.secondary' }}>
                      {preparing ? <CircularProgress size={22} /> : <AddIcon />}
                      <Typography variant="body2">
                        {atMax ? `Max ${MAX_SCENES_PER_ROOM}` : preparing ? 'Reading status…' : 'Add Scene'}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {toastNode}
    </Box>
  );
}
